import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-black flex flex-col">
      <Navigation />
      <main className="flex-1 pt-24 pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-500 text-sm mb-2">Last Updated: February 8, 2026</p>
          <h1 className="text-3xl font-bold text-white mb-10">Propalgos.ai Terms of Service</h1>

          <div className="prose prose-invert prose-sm max-w-none space-y-8 text-gray-300 leading-relaxed">
            {/* Section 1 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">1. Acceptance of Terms</h2>
              <p>
                By creating an account, accessing, or using the Propalgos.ai platform
                ("Platform"), you agree to be bound by these Terms of Service ("Terms"). If
                you enter into these Terms on behalf of an entity, you represent that you have
                the legal authority to bind that entity.
              </p>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">2. Nature of Services; No Financial Advice</h2>

              <h3 className="text-base font-medium text-gray-200 mt-4 mb-2">2.1 Technology Provider Only</h3>
              <p>
                Propalgos provides high-performance computing resources, data access, and
                software tools for quantitative analysis. We are a technology provider, not a
                financial institution, registered investment advisor, or broker-dealer.
              </p>

              <h3 className="text-base font-medium text-gray-200 mt-4 mb-2">2.2 No Investment Recommendations</h3>
              <p>
                Nothing on the Platform constitutes a recommendation to buy, sell, or hold any
                security or financial instrument. All analysis, signals, or strategy outputs
                generated via the Platform are produced solely by your own configuration of
                algorithms and data. The "GenAI Augmented Summary" features are for
                informational purposes only and do not constitute fiduciary advice.
              </p>

              <h3 className="text-base font-medium text-gray-200 mt-4 mb-2">2.3 Hypothetical Performance Disclaimer (CFTC Rule 4.41)</h3>
              <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-4 my-4">
                <p className="text-yellow-200/90 text-xs font-bold uppercase leading-relaxed tracking-wide">
                  CFTC RULE 4.41 — HYPOTHETICAL OR SIMULATED PERFORMANCE RESULTS HAVE
                  CERTAIN INHERENT LIMITATIONS. UNLIKE AN ACTUAL PERFORMANCE RECORD,
                  SIMULATED RESULTS DO NOT REPRESENT ACTUAL TRADING. ALSO, SINCE THE TRADES
                  HAVE NOT ACTUALLY BEEN EXECUTED, THE RESULTS MAY HAVE UNDER-OR-OVER
                  COMPENSATED FOR THE IMPACT, IF ANY, OF CERTAIN MARKET FACTORS, SUCH AS LACK
                  OF LIQUIDITY. SIMULATED TRADING PROGRAMS IN GENERAL ARE ALSO SUBJECT TO THE
                  FACT THAT THEY ARE DESIGNED WITH THE BENEFIT OF HINDSIGHT. NO REPRESENTATION
                  IS BEING MADE THAT ANY ACCOUNT WILL OR IS LIKELY TO ACHIEVE PROFITS OR
                  LOSSES SIMILAR TO THOSE SHOWN.
                </p>
              </div>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">3. Cloud Infrastructure & Reseller Terms</h2>

              <h3 className="text-base font-medium text-gray-200 mt-4 mb-2">3.1 Upstream Providers</h3>
              <p>
                The Platform provisions computing resources (including GPUs) from third-party
                cloud providers, including but not limited to Amazon Web Services (AWS) and
                Google Cloud Platform (GCP) (collectively, "Upstream Providers").
              </p>

              <h3 className="text-base font-medium text-gray-200 mt-4 mb-2">3.2 Pass-Through of Service Levels</h3>
              <p>
                Propalgos does not own or control the physical infrastructure of the Upstream
                Providers.{' '}
                <strong className="text-white">
                  WE DO NOT WARRANT THE UPTIME, AVAILABILITY, OR PERFORMANCE OF THE UPSTREAM
                  PROVIDERS.
                </strong>{' '}
                Any Service Level Agreement (SLA) credits issued by an Upstream Provider to
                Propalgos for downtime affecting your instances may be passed through to you
                at our sole discretion, in the form of Platform credits, pro-rated to your
                usage. You acknowledge that if the Upstream Provider denies a claim for
                service credits, Propalgos has no obligation to provide such credits to you.
              </p>

              <h3 className="text-base font-medium text-gray-200 mt-4 mb-2">3.3 Data Center Locations</h3>
              <p>
                You acknowledge that your data and compute workloads may be processed in
                various geographic regions as determined by the Upstream Providers'
                availability and your configuration settings.
              </p>

              <h3 className="text-base font-medium text-gray-200 mt-4 mb-2">3.4 Shared Responsibility Model</h3>
              <p>
                You agree that security is a shared responsibility. While Propalgos secures
                the platform application layer, you are solely responsible for the security of
                your code, API keys, and the configuration of your compute instances (e.g.,
                ensuring open ports are not accessible to the public internet).
              </p>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">4. Metering, Billing, and Payments</h2>

              <h3 className="text-base font-medium text-gray-200 mt-4 mb-2">4.1 Pay-As-You-Go Metering</h3>
              <p>
                You agree to pay for all compute resources, storage, and data ingress/egress
                incurred by your account. Usage is metered in increments defined in our
                Pricing Page.
              </p>

              <h3 className="text-base font-medium text-gray-200 mt-4 mb-2">4.2 Responsibility for Running Instances</h3>
              <p>
                <strong className="text-white">
                  IT IS YOUR SOLE RESPONSIBILITY TO TERMINATE INSTANCES WHEN NOT IN USE.
                </strong>{' '}
                You are responsible for all charges generated by active resources, regardless
                of whether you are actively running computations. Propalgos is not liable for
                "zombie" instances left running by the user. We reserve the right, but have no
                obligation, to terminate instances that exceed your pre-paid credit balance.
              </p>

              <h3 className="text-base font-medium text-gray-200 mt-4 mb-2">4.3 No Refunds for Compute</h3>
              <p>
                Due to the direct costs incurred from Upstream Providers, all compute charges
                are final and non-refundable, even if the code executed on the compute fails,
                produces no results, or if the user forgets to terminate the instance.
              </p>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">5. Acceptable Use Policy (AUP)</h2>
              <p>You agree not to use the Platform for:</p>
              <ul className="list-disc list-inside space-y-2 mt-2 text-gray-400">
                <li>
                  <strong className="text-gray-300">Cryptocurrency Mining:</strong> Mining
                  Bitcoin, Ethereum, or any other proof-of-work cryptocurrency is strictly
                  prohibited unless explicitly authorized in writing. Violation will result in
                  immediate termination and forfeiture of credits.
                </li>
                <li>
                  <strong className="text-gray-300">Malware & Scanning:</strong> Distributing
                  malware, viruses, or executing port scanning, vulnerability testing, or
                  penetration testing against any target (including Upstream Providers) without
                  authorization.
                </li>
                <li>
                  <strong className="text-gray-300">Illegal Activities:</strong> Facilitating
                  illegal gambling, money laundering, or intellectual property infringement.
                </li>
                <li>
                  <strong className="text-gray-300">Platform Abuse:</strong> Creating multiple
                  accounts to bypass free tier limits or competition entry restrictions.
                </li>
              </ul>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">6. User Content & Intellectual Property</h2>

              <h3 className="text-base font-medium text-gray-200 mt-4 mb-2">6.1 Your Strategies</h3>
              <p>
                You retain ownership of the algorithms, code, and trading strategies you
                create on the Platform.
              </p>

              <h3 className="text-base font-medium text-gray-200 mt-4 mb-2">6.2 License to Propalgos</h3>
              <p>
                You grant Propalgos a limited, worldwide, non-exclusive license to host,
                execute, and display your Content solely as necessary to provide the Services.
              </p>

              <h3 className="text-base font-medium text-gray-200 mt-4 mb-2">6.3 Competition Entries</h3>
              <p>
                If you submit a strategy to a "Weekly Winner" or similar competition, you
                grant Propalgos a license to display the performance metrics, code (if
                applicable based on specific contest rules), and backtest results of the entry
                for marketing and leaderboard purposes. You represent that your entry is your
                original work and does not infringe on the IP of third parties.
              </p>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">7. Limitation of Liability</h2>
              <p className="font-semibold text-gray-200">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, PROPALGOS.AI AND ITS SUPPLIERS
                (INCLUDING AWS AND GOOGLE) SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
                SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR
                REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE,
                GOODWILL, OR OTHER INTANGIBLE LOSSES.
              </p>
              <p className="mt-3 font-semibold text-gray-200">
                IN NO EVENT SHALL PROPALGOS'S AGGREGATE LIABILITY EXCEED THE AMOUNT YOU PAID
                TO PROPALGOS IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM. THIS LIMITATION
                APPLIES REGARDLESS OF WHETHER THE ALLEGED LIABILITY IS BASED ON CONTRACT,
                TORT, NEGLIGENCE, STRICT LIABILITY, OR ANY OTHER BASIS.
              </p>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">8. Dispute Resolution and Class Action Waiver</h2>

              <h3 className="text-base font-medium text-gray-200 mt-4 mb-2">8.1 Governing Law</h3>
              <p>
                These Terms shall be governed by the laws of the State of Delaware, without
                regard to conflict of law principles.
              </p>

              <h3 className="text-base font-medium text-gray-200 mt-4 mb-2">8.2 Arbitration</h3>
              <p>
                Any dispute arising under these Terms shall be resolved via binding
                arbitration administered by the American Arbitration Association (AAA).
              </p>

              <h3 className="text-base font-medium text-gray-200 mt-4 mb-2">8.3 Class Action Waiver</h3>
              <p className="font-semibold text-gray-200">
                YOU AND PROPALGOS AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN
                YOUR OR ITS INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY
                PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
